import { APP_VERSION } from "../appVersion";
import {
  VISION_RUNTIME_PARAMETER_CATALOG_SHA256,
  VISION_RUNTIME_PARAMETER_CATALOG_VERSION,
} from "../generated/visionRuntimeParameters";
import { getLabelDetectionWorkerDiagnostic } from "./labelDetectionWorkerService";
import { getLabelOcrRuntimeDiagnostic } from "./labelOcrService";

const STORAGE_KEY = "materialentnahme.scan.lastFailureFlight.v1";

export interface ScanFailureFlightStep {
  id: string;
  label: string;
  status: string;
  detail: string | null;
  durationMs: number | null;
}

export interface ScanFailureFlightRecord {
  schemaVersion: 1;
  contractVersion: "scan-failure-flight-recorder-v1";
  recordedAtUtc: string;
  appVersion: string;
  recognitionRunId: string | null;
  status: "checkpoint" | "failure";
  activeStep: string;
  elapsedMs: number | null;
  errorName: string | null;
  errorMessage: string | null;
  workerDiagnostic: ReturnType<typeof getLabelDetectionWorkerDiagnostic>;
  ocrRuntimeDiagnostic: ReturnType<typeof getLabelOcrRuntimeDiagnostic>;
  runtimeCatalogVersion: string;
  runtimeCatalogSha256: string;
  device: {
    platform: string;
    userAgent: string;
    logicalCpuCount: number | null;
    online: boolean | null;
    visibilityState: string | null;
    secureContext: boolean | null;
  };
  steps: ScanFailureFlightStep[];
}

function safeSet(value: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Diagnose darf den Scanfehler niemals überdecken.
  }
}

export function clearLastScanFailureFlight(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best effort.
  }
}

function buildRecord(input: {
  recognitionRunId?: string | null;
  status: "checkpoint" | "failure";
  activeStep: string;
  pipelineStartedAt?: number | null;
  error?: unknown;
  steps: ScanFailureFlightStep[];
}): ScanFailureFlightRecord {
  const error = input.status === "failure"
    ? (input.error instanceof Error ? input.error : new Error(String(input.error)))
    : null;
  const record: ScanFailureFlightRecord = {
    schemaVersion: 1,
    contractVersion: "scan-failure-flight-recorder-v1",
    recordedAtUtc: new Date().toISOString(),
    appVersion: APP_VERSION,
    recognitionRunId: input.recognitionRunId ?? null,
    status: input.status,
    activeStep: input.activeStep,
    elapsedMs: typeof input.pipelineStartedAt === "number"
      ? Math.max(0, performance.now() - input.pipelineStartedAt)
      : null,
    errorName: error?.name || null,
    errorMessage: error ? (error.message || String(input.error)) : null,
    workerDiagnostic: getLabelDetectionWorkerDiagnostic(),
    ocrRuntimeDiagnostic: getLabelOcrRuntimeDiagnostic(),
    runtimeCatalogVersion: VISION_RUNTIME_PARAMETER_CATALOG_VERSION,
    runtimeCatalogSha256: VISION_RUNTIME_PARAMETER_CATALOG_SHA256,
    device: {
      platform: navigator.platform || "unknown",
      userAgent: navigator.userAgent || "unknown",
      logicalCpuCount: typeof navigator.hardwareConcurrency === "number" ? navigator.hardwareConcurrency : null,
      online: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
      visibilityState: typeof document.visibilityState === "string" ? document.visibilityState : null,
      secureContext: typeof window.isSecureContext === "boolean" ? window.isSecureContext : null,
    },
    steps: input.steps.map((step) => ({ ...step })),
  };
  safeSet(JSON.stringify(record));
  return record;
}

export function recordScanFlightCheckpoint(input: {
  recognitionRunId?: string | null;
  activeStep: string;
  pipelineStartedAt?: number | null;
  steps: ScanFailureFlightStep[];
}): ScanFailureFlightRecord {
  return buildRecord({ ...input, status: "checkpoint" });
}

export function recordScanFailureFlight(input: {
  recognitionRunId?: string | null;
  activeStep: string;
  pipelineStartedAt?: number | null;
  error: unknown;
  steps: ScanFailureFlightStep[];
}): ScanFailureFlightRecord {
  return buildRecord({ ...input, status: "failure" });
}

export function getLastScanFailureFlight(): ScanFailureFlightRecord | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScanFailureFlightRecord>;
    if (parsed.schemaVersion !== 1 || parsed.contractVersion !== "scan-failure-flight-recorder-v1") return null;
    return {
      ...(parsed as ScanFailureFlightRecord),
      status: parsed.status === "checkpoint" ? "checkpoint" : "failure",
      errorName: typeof parsed.errorName === "string" ? parsed.errorName : null,
      errorMessage: typeof parsed.errorMessage === "string" ? parsed.errorMessage : null,
    };
  } catch {
    return null;
  }
}
